import { doc, getDocFromServer, onSnapshot } from "firebase/firestore";
import { db, appCheck } from "../firebaseConfig";
import { getToken } from "firebase/app-check";
import { getOrganizationLookupCandidates } from "../utils/organizationAliases";
import {
  readPublicRecord,
  projectTaxStatus,
  ID,
} from "./publicOrganizationContract";
import { removeLegacyPublicOrganizationCaches } from "../utils/removeLegacyOrganizationCaches";
import { boundedOrganizationResolution } from "../utils/organizationResolution";
export { removeLegacyPublicOrganizationCaches } from "../utils/removeLegacyOrganizationCaches";

async function readServerDocument(reference) {
  // Let slow attestation finish before starting Firestore's connectivity probe.
  // A rejected attestation remains an error; it is never evidence of absence.
  return boundedOrganizationResolution(async (isActive) => {
    if (appCheck) await getToken(appCheck);
    // An expired attestation must not start a late Firestore lookup.
    if (!isActive()) return undefined;
    return getDocFromServer(reference);
  });
}

export async function attachPublicTaxStatus(organization, readDocument) {
  if (!organization) return null;
  let decision;
  try {
    const record = await readDocument(
      "publicOrganizationTaxStatus/" + organization.id,
    );
    if (
      record?.schemaVersion === 1 &&
      record.organizationId === organization.id &&
      record.status === "published"
    )
      decision = record.taxStatus;
  } catch {
    /* Tax availability never takes a website offline. */
  }
  return { ...organization, taxStatus: projectTaxStatus(decision) };
}

// No persistent cache or internal-document fallback: cached content cannot
// establish that a website is still published.
export async function readPublicOrganizationRoute(key) {
  if (!/^(id|alias|domain):[^/]{1,253}$/.test(key)) return null;
  const snapshot = await readServerDocument(
    doc(db, "publicOrganizationRoutes", key),
  );
  if (!snapshot.exists()) return null;
  const r = snapshot.data();
  if (
    Object.keys(r).some(
      (field) =>
        ![
          "schemaVersion",
          "organizationId",
          "revision",
          "status",
          "key",
        ].includes(field),
    )
  )
    return null;
  return r.schemaVersion === 1 &&
    r.key === key &&
    r.status === "published" &&
    typeof r.organizationId === "string" &&
    ID.test(r.organizationId)
    ? r
    : null;
}
// Reuse the same route/public-record contract with an injected raw-document
// transport (for governed giving resolution and Rules-enforced integration).
// The transport must never fall back to an internal organization document.
export async function readPublicOrganizationWith(readDocument, slug) {
  removeLegacyPublicOrganizationCaches();
  for (const candidate of getOrganizationLookupCandidates(slug)) {
    if (!ID.test(candidate)) continue;
    for (const key of [`id:${candidate}`, `alias:${candidate}`]) {
      const route = await readDocument(`publicOrganizationRoutes/${key}`);
      if (
        !route ||
        route.schemaVersion !== 1 ||
        route.key !== key ||
        route.status !== "published" ||
        typeof route.organizationId !== "string" ||
        !ID.test(route.organizationId) ||
        Object.keys(route).some(
          (field) =>
            ![
              "schemaVersion",
              "organizationId",
              "revision",
              "status",
              "key",
            ].includes(field),
        )
      )
        continue;
      const data = await readDocument(
        `publicOrganizations/${route.organizationId}`,
      );
      return attachPublicTaxStatus(
        readPublicRecord(data, route.organizationId),
        readDocument,
      );
    }
  }
  return null;
}
export async function resolvePublicOrganizationId(slug) {
  for (const id of getOrganizationLookupCandidates(slug)) {
    if (!ID.test(id)) continue;
    for (const key of [`id:${id}`, `alias:${id}`]) {
      const route = await readPublicOrganizationRoute(key);
      if (route) return route.organizationId;
    }
  }
  return "";
}
export async function getPublicOrganization(slug) {
  removeLegacyPublicOrganizationCaches();
  const id = await resolvePublicOrganizationId(slug);
  if (!id) return null;
  const snapshot = await readServerDocument(doc(db, "publicOrganizations", id));
  return attachPublicTaxStatus(
    snapshot.exists() ? readPublicRecord(snapshot.data(), id) : null,
    async (path) => {
      const snap = await readServerDocument(doc(db, path));
      return snap.exists() ? snap.data() : null;
    },
  );
}
export function subscribePublicOrganization(slug, next, error) {
  removeLegacyPublicOrganizationCaches();
  let cancelled = false;
  let unsubscribe, taxUnsubscribe;
  let orgValue = null,
    taxValue = null;
  const emit = () => {
    if (!cancelled)
      next(
        orgValue
          ? { ...orgValue, taxStatus: projectTaxStatus(taxValue) }
          : null,
      );
  };
  resolvePublicOrganizationId(slug)
    .then((id) => {
      if (cancelled) return;
      if (!id) {
        next(null);
        return;
      }
      unsubscribe = onSnapshot(
        doc(db, "publicOrganizations", id),
        { includeMetadataChanges: true },
        (snap) => {
          if (
            cancelled ||
            snap.metadata?.fromCache ||
            snap.metadata?.hasPendingWrites
          )
            return;
          orgValue = snap.exists() ? readPublicRecord(snap.data(), id) : null;
          emit();
        },
        (err) => {
          if (!cancelled) error(err);
        },
      );
      taxUnsubscribe = onSnapshot(
        doc(db, "publicOrganizationTaxStatus", id),
        { includeMetadataChanges: true },
        (snap) => {
          if (
            cancelled ||
            snap.metadata?.fromCache ||
            snap.metadata?.hasPendingWrites
          )
            return;
          const r = snap.exists() ? snap.data() : null;
          taxValue =
            r?.schemaVersion === 1 &&
            r.organizationId === id &&
            r.status === "published"
              ? r.taxStatus
              : null;
          emit();
        },
        () => {
          taxValue = null;
          emit();
        },
      );
    })
    .catch((err) => {
      if (!cancelled) error(err);
    });
  return () => {
    cancelled = true;
    unsubscribe?.();
    taxUnsubscribe?.();
  };
}
